import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuthImage } from "@/hooks/useAuthImage";
import { bubblesToText, parseBubbleText } from "./bubbleText";
import BubbleEditor from "./bubbles/BubbleEditor";
import PlancheLightbox from "./PlancheLightbox";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/bd`
  : "/api/bd";

function parseImageUrl(imageUrl) {
  // /api/bd/chapters/<client_id>-<chapter_index>/versions/<version_id>/image
  const m = imageUrl.match(/chapters\/(.+?)-(\d+)\/versions\/([^/]+)\/image/);
  if (!m) return null;
  return { client_id: m[1], chapter_index: parseInt(m[2], 10), version_id: m[3] };
}

export function pickBubbleMode(version) {
  return version && version.editable ? "editor" : "textarea";
}

export default function ImageMessage({ token, imageUrl, onRefresh, onRegen }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [bubbleText, setBubbleText] = useState("");
  const [mode, setMode] = useState("textarea"); // 'editor' | 'textarea'
  const [layout, setLayout] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Cache-bust : l'édition de bulles EN PLACE réécrit l'image à la MÊME URL.
  // useAuthImage ne re-fetch que si l'URL change → on fait varier l'URL après save.
  const [bust, setBust] = useState(0);
  const meta = parseImageUrl(imageUrl);
  const displayUrl = bust
    ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}t=${bust}`
    : imageUrl;
  const { src, error } = useAuthImage(displayUrl, token);
  // URL du dessin nu (art) pour l'éditeur de bulles Option B.
  // Même mécanisme d'auth que displayUrl (useAuthImage fetche en blob avec Bearer).
  // Seul l'éditeur (mode=editor) l'utilise ; la vue chat reste sur src (image cuite).
  const artDisplayUrl = displayUrl ? displayUrl.replace(/\/image(\?.*)?$/, "/art$1") : null;
  const { src: artSrc } = useAuthImage(artDisplayUrl, token);

  const doAction = async (path, body) => {
    if (!meta || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/chapters/${meta.client_id}-${meta.chapter_index}/versions/${meta.version_id}/${path}`,
        body || {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (path === "rate") toast.success("Merci du retour !");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Régénération d'image (variation / édition de bulles) : l'opération tourne en
  // TÂCHE DE FOND côté serveur (gpt-image-2 ~1-3 min) et répond aussitôt
  // {status:"running"}. On bascule alors sur le suivi du chat (chrono + polling
  // du job), et l'image régénérée remonte comme nouveau message quand c'est prêt.
  // Renvoie true si le suivi a démarré.
  //
  // B1 : le compte-à-rebours ne démarre QUE si le POST confirme le lancement
  // (réponse 2xx). Un 409 (opération déjà en cours) ou une erreur réseau affiche
  // un toast et n'enclenche PAS le suivi — évite le décompte silencieux quand la
  // requête n'a pas atteint le serveur.
  const kickRegen = async (path, body) => {
    if (!meta || busy) return false;
    setBusy(true);
    try {
      await axios.post(
        `${API}/chapters/${meta.client_id}-${meta.chapter_index}/versions/${meta.version_id}/${path}`,
        body || {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // POST confirmé 2xx → on démarre le suivi.
      if (onRegen) onRegen();
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        // Une opération tourne déjà : informer l'utilisateur sans démarrer le
        // décompte (le chat suit déjà la génération en cours s'il était ouvert,
        // sinon un rechargement de page reprendra le suivi automatiquement).
        toast.warning(
          "Une régénération est déjà en cours pour ce client, " +
          "patiente qu'elle se termine."
        );
      } else {
        // Erreur réseau ou autre non-2xx : on n'a pas de confirmation que le
        // job a démarré → ne pas lancer le décompte.
        toast.error(
          e?.response?.data?.detail ||
          "Échec du lancement de la régénération — vérifie ta connexion et réessaie."
        );
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    if (!window.confirm("Valider cette version comme officielle du chapitre ?")) return;
    await doAction("validate");
  };

  const handleRegenerate = async () => {
    // eslint-disable-next-line no-alert
    const instruction = window.prompt(
      "Instruction de variation (ex: 'plan plus serré sur le visage') :",
    );
    if (!instruction || !instruction.trim()) return;
    await kickRegen("regenerate", { instruction: instruction.trim() });
  };

  const handleFullRedraw = async () => {
    // eslint-disable-next-line no-alert
    const instruction = window.prompt(
      "Que faut-il changer ? La planche sera redessinée de zéro dans le style du dossier, sans repartir du dessin actuel. Les retouches de bulles faites à la main seront perdues.",
    );
    if (!instruction || !instruction.trim()) return;
    await kickRegen("regenerate", {
      instruction: instruction.trim(),
      full_redraw: true,
    });
  };

  const handleRedrawFromSource = async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      "Le chapitre sera relu et la planche refaite à partir du texte actuel. Les cases seront différentes de celles de la version affichée. À utiliser quand le texte du chapitre a été modifié.",
    );
    if (!confirmed) return;
    await kickRegen("redraw-from-source");
  };

  // Ouvre l'éditeur de bulles inline, PRÉ-REMPLI avec les bulles existantes.
  // Aiguille vers BubbleEditor (Option B, editable=true) ou textarea (legacy).
  const openBubbleEditor = async () => {
    if (!meta || busy) return;
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/chapters/${meta.client_id}-${meta.chapter_index}/versions`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const v = (r.data?.versions || []).find((x) => x.version_id === meta.version_id);
      const m = pickBubbleMode(v);
      setMode(m);
      if (m === "editor") setLayout(v.bubble_layout || []);
      else setBubbleText(bubblesToText(v?.bubbles));
      setEditing(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveBubbles = async () => {
    const ok = await kickRegen("update-bubbles", { new_bubbles: parseBubbleText(bubbleText) });
    if (ok) setEditing(false);
  };

  // Synchronous save for Option B (editable versions) — no job, no regen.
  // Édition EN PLACE : le backend réécrit la MÊME version (même URL). On ne
  // recharge donc PAS la conversation (rien n'y change), on force juste le
  // refetch de l'image via le cache-bust pour voir le nouveau rendu tout de suite.
  const saveLayout = async (bubbles) => {
    if (!meta || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/chapters/${meta.client_id}-${meta.chapter_index}/versions/${meta.version_id}/bubbles`,
        { bubbles },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEditing(false);
      setBust(Date.now());
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="text-red-600 text-sm">Erreur image : {error}</div>;
  if (!src) return <div className="text-gray-400 text-sm">Chargement image…</div>;

  return (
    <div className="my-2">
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className="block max-w-full rounded border cursor-zoom-in bg-transparent p-0 text-left"
        aria-label="Agrandir la planche BD"
      >
        <img src={src} alt="BD générée" className="max-w-full rounded border cursor-zoom-in" />
      </button>
      {lightboxOpen && (
        <PlancheLightbox
          src={src}
          alt="BD générée"
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {meta && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleValidate}
            disabled={busy}
            className="bg-green-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-green-700"
          >
            {busy ? "…" : "✓ Valider"}
          </button>
          <button
            onClick={handleRegenerate}
            disabled={busy}
            className="bg-orange-500 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-orange-600"
          >
            {busy ? "…" : "🔄 Régénérer"}
          </button>
          <button
            onClick={handleFullRedraw}
            disabled={busy}
            title="Redessine la planche de zéro dans le style du dossier, sans repartir du dessin actuel. Ne change pas le style choisi du dossier. Les retouches de bulles faites à la main sont perdues."
            className="bg-orange-500 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-orange-600"
          >
            {busy ? "…" : "🎨 Refaire le dessin"}
          </button>
          <button
            onClick={handleRedrawFromSource}
            disabled={busy}
            title="Le chapitre sera relu et la planche refaite à partir du texte actuel. Les cases seront différentes de celles de la version affichée. À utiliser quand le texte du chapitre a été modifié."
            className="bg-orange-500 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-orange-600"
          >
            {busy ? "…" : "📖 Repartir du texte"}
          </button>
          <button
            onClick={openBubbleEditor}
            disabled={busy}
            className="bg-blue-500 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-blue-600"
          >
            {busy ? "…" : "✏️ Modifier bulles"}
          </button>
          <button
            onClick={() => doAction("rate", { feedback: "up" })}
            disabled={busy}
            className="bg-gray-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-gray-700"
          >
            {busy ? "…" : "👍"}
          </button>
          <button
            onClick={() => doAction("rate", { feedback: "down" })}
            disabled={busy}
            className="bg-gray-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-gray-700"
          >
            {busy ? "…" : "👎"}
          </button>
        </div>
      )}
      {editing && mode === "editor" && (
        <BubbleEditor
          src={artSrc || src}
          initialLayout={layout}
          busy={busy}
          onSave={saveLayout}
          onCancel={() => setEditing(false)}
        />
      )}
      {editing && mode === "textarea" && (
        <div className="mt-2 border rounded p-2 bg-gray-50">
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-2">
            ℹ️ Cette BD date d'<strong>avant l'éditeur visuel de bulles</strong>. Ici tu peux
            seulement <strong>corriger le texte</strong> (l'image se régénère, ~1-2 min).
            Pour <strong>déplacer ou supprimer une bulle au clic</strong>, <strong>régénère la
            BD</strong> (bouton « 🔄 Régénérer ») : les nouvelles versions seront éditables visuellement.
          </div>
          <p className="text-xs text-gray-600 mb-1">
            Une bulle par ligne. Pour une narration, préfixe la ligne par « Narration: ».
          </p>
          <textarea
            value={bubbleText}
            onChange={(e) => setBubbleText(e.target.value)}
            rows={Math.max(3, bubbleText.split("\n").length + 1)}
            className="w-full text-sm border rounded p-2 font-mono"
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={saveBubbles}
              disabled={busy}
              className="bg-green-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-green-700"
            >
              {busy ? "…" : "Enregistrer"}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={busy}
              className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded disabled:opacity-50 hover:bg-gray-300"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
